import { DeleteIcon } from "@chakra-ui/icons";
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useDeleteView } from "features/transactions/hooks/useDeleteView";
import { useVisibilitySelector } from "features/transactions/TableViewCreateOrUpdateModal/VisibilitySelector";
import {
  useTableViewCreate,
  useTableViewUpdate,
} from "gen/endpoints/table-views/table-views";
import { TableParamsTypeEnum } from "gen/model/tableParamsTypeEnum";
import { TableViewReadData } from "gen/model/tableViewReadData";
import { TableViewUpdateAPIDataRequest } from "gen/model/tableViewUpdateAPIDataRequest";
import { tableViewUpdateBody } from "gen/schemas/table-views/table-views";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Form from "shared/components/form/Form";
import { FormTextInput } from "shared/components/input/text/TextInput";
import { rootRoute } from "shared/navigation/Routes";

export type EditFormValues = Pick<
  TableViewUpdateAPIDataRequest,
  "name" | "visible_to"
>;

export const TableViewCreateOrUpdateModal = ({
  existingViewId,
  isOpen,
  onClose,
  getViewState,
  onSuccess,
  viewDetails,
}: {
  existingViewId?: string;
  isOpen: boolean;
  onClose: () => void;
  getViewState: () => string | Promise<string>;
  viewType: TableParamsTypeEnum;
  onSuccess?: () => void;
  viewDetails?: TableViewReadData | undefined;
}) => {
  const { t } = useTranslation(["common", "views"]);
  const { isPending: updatePending, mutateAsync: updateTableView } =
    useTableViewUpdate();
  const { isPending: createPending, mutateAsync: createTableView } =
    useTableViewCreate();

  const navigate = useNavigate();
  const form = useForm<EditFormValues>({
    resolver: zodResolver(
      tableViewUpdateBody.pick({ name: true, visible_to: true }),
    ),
    defaultValues: {
      visible_to: "",
      name: "",
    },
  });

  const { permOptions, visibilitySelector } = useVisibilitySelector({ form });

  // always reset the edit modal with new view details
  useEffect(() => {
    if (viewDetails) {
      form.reset({
        ...viewDetails,
        visible_to: viewDetails.visible_to
          ? permOptions[0].value
          : permOptions[1].value,
      });
    }
  }, [viewDetails, form, permOptions]);

  const onSubmit = async (formValues: EditFormValues) => {
    const requestData: TableViewUpdateAPIDataRequest = {
      ...formValues,
      visible_to: formValues.visible_to || null,
      encoded_table_params: await getViewState(),
    };

    if (existingViewId) {
      const view = await updateTableView({
        viewId: existingViewId,
        data: requestData,
      });
      return view;
    } else {
      const view = await createTableView({
        data: {
          ...requestData,
          type: TableParamsTypeEnum.transaction_table,
        },
      });
      return view;
    }
  };

  const { deleteView, isDeleting } = useDeleteView({ existingViewId });

  return (
    <Modal isOpen={isOpen} onClose={onClose} scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <Form
          form={form}
          onSubmit={onSubmit}
          onSuccess={(viewUpdate) => {
            onSuccess?.();
            onClose();
            navigate(
              rootRoute({})
                .transactions({})
                .tableDetails({ viewId: viewUpdate.id }).$,
            );
          }}
        >
          <ModalHeader>
            {existingViewId
              ? t("views:modal.editHeader")
              : t("views:modal.createHeader")}
            <ModalCloseButton onClick={onClose} />
          </ModalHeader>
          <ModalBody pb={40}>
            <Flex flexDirection="column" rowGap="4px">
              <FormTextInput
                shouldUnregister={false}
                title={t("views:modal.nameLabel")}
                name="name"
              />
              {visibilitySelector}
            </Flex>
          </ModalBody>

          <ModalFooter
            justifyContent={existingViewId ? "space-between" : "flex-end"}
          >
            {existingViewId && (
              <Button
                colorScheme="destructive"
                variant="ghost"
                key="delete"
                onClick={deleteView}
                isLoading={isDeleting}
                leftIcon={<DeleteIcon />}
              >
                {t("common:delete")}
              </Button>
            )}
            <Flex gap={2}>
              <Button colorScheme="secondary" onClick={onClose}>
                {t("common:cancel")}
              </Button>
              <Button isLoading={updatePending || createPending} type="submit">
                {existingViewId
                  ? t("views:actions.saveUpdates")
                  : t("views:actions.saveAsNew")}
              </Button>
            </Flex>
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
};
